import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Box,
  TextField as MuiTextField
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../../src/components';
import { BASE_URL, dropdownFetch, isToday, request, toast } from '../../../../../../src/utils';
import axios from 'axios';
import { SelectDoctorTable } from '../masters/others/SelectDoctorTable';
import { useDoctorScope } from '../../../../../../src/customHooks/useDoctorScope';

const DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row.name_suffix} ${row.first_name} ${row.last_name ? row.last_name : ''}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Token No',
    accessor: 'token_no'
  },
  {
    header: 'Doctor Name',
    accessor: 'consultant.name'
  },
  {
    header: 'Address',
    accessor: 'address.address'
  },
  {
    header: 'Status',
    accessor: (row) => (row.opd_status === 'serving' ? 'In Consultation' : 'Waiting'),
    sx: { textTransform: 'uppercase' }
  }
];

const DONE_DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row.name_suffix} ${row.first_name} ${row.last_name ? row.last_name : ''}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Token No',
    accessor: 'token_no'
  },
  {
    header: 'Doctor Name',
    accessor: 'consultant.name'
  },
  {
    header: 'Address',
    accessor: 'address.address'
  }
];

const DEFER_DETAIL_HEADER = [
  {
    header: 'Patient Name',
    accessor: (row) => `${row.name_suffix} ${row.first_name} ${row.last_name ? row.last_name : ''}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Token No',
    accessor: 'token_no'
  },
  {
    header: 'Doctor Name',
    accessor: 'consultant.name'
  },
  {
    header: 'Address',
    accessor: 'address.address'
  }
];


const initialIndoorStatusDataObj = {
  data: [],
  loading: false
};

export const QueueManagementDetails = () => {
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [users, setUsers] = useState([]);
  const [isUserListLoaded, setIsUserListLoaded] = useState(false);
  const [opdDoneList, setOpdDoneList] = useState([]);
  const [deferList, setDeferList] = useState([]);

  const user = JSON.parse(localStorage.getItem('user'));
  const methods = useForm({
    defaultValues: {},
    mode: 'all'
  });
  const { handleSubmit, setValue, reset, watch } = methods;

  const watchDoctors = watch('doctor');
  const doctorScope = useDoctorScope();

  useEffect(() => {
    if (doctorScope.isDoctor && doctorScope.doctorId) {
      setValue('doctor', doctorScope.doctorId);
    }
  }, [doctorScope.isDoctor, doctorScope.doctorId, setValue]);

  useEffect(() => {
    searchPatientDetails();
  }, [watchDoctors]);

  const searchPatientDetails = async () => {
    const lowercasedInput = watchDoctors;
    setLoading(true);
    const query = {};
    if (lowercasedInput) {
      query.patientName = lowercasedInput;
    }
    const response = await request('/patient-registeration-today-list', {
      method: 'GET',
      query: query
    });
    if (response?.success) {
      if (!response?.patientData) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setDetailList(response?.patientData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  const OpdDonePatientDetails = async () => {
    const lowercasedInput = watchDoctors;
    setLoading(true);
    const query = {};
    if (lowercasedInput) {
      query.patientName = lowercasedInput;
    }
    const response = await request('/patient-registeration-done-list', {
      method: 'GET',
      query: query
    });
    if (response?.success) {
      if (!response?.patientData) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setOpdDoneList(response?.patientData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };


  const DeferPatientDetails = async () => {
    const lowercasedInput = watchDoctors;
    setLoading(true);
    const query = {};
    if (lowercasedInput) {
      query.patientName = lowercasedInput;
    }
    const response = await request('/patient-registeration-defer-list', {
      method: 'GET',
      query: query
    });
    if (response?.success) {
      if (!response?.patientData) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        setDeferList(response?.patientData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    OpdDonePatientDetails();
    DeferPatientDetails();
  }, []);


  const getUserList = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/user`);
      const filteredUsers = response.data.filter((data) => {
        const [firstName, lastName] = user.name.split(' ');
        return data.first_name === firstName && data.last_name === lastName;
      });
      setUsers(filteredUsers[0]?._id);
      setIsUserListLoaded(true);
    } catch (error) {
      console.error('Error fetching user list:', error);
      setIsUserListLoaded(true);
    }
  };

  const handleDoctorDropdown = async () => {
    if (!isUserListLoaded) return;
    setDoctorDropdown({ loading: true, data: [] });
    try {
      if (user?.superadmin) {
        const response = await request('/doctor-dropdown', { method: 'GET' });
        if (response.success) {
          setDoctorDropdown({ loading: false, data: response.data });
        } else {
          toast('Error in fetching doctors', { variant: 'error' });
          setDoctorDropdown({ loading: false, data: [] });
        }
        return;
      }
      const query = { name: users };
      const response = await request('/user-wise-doctor-mapping-list', {
        method: 'GET',
        query: query
      });
      if (response.success) {
        setDoctorDropdown({ loading: false, data: response.data.flatMap((item) => item.selectedDoctors) });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
        setDoctorDropdown({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setDoctorDropdown({ loading: false, data: [] });
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    if (isUserListLoaded) {
      handleDoctorDropdown();
    }
  }, [isUserListLoaded]);

  const handleCallIn = async (id) => {
    setLoading(true);
    const response = await request('/patient-registeration-call-in', {
      method: 'PUT',
      params: id
    });
    if (response?.success) {
      toast('Patient called in', { variant: 'success' });
      searchPatientDetails();
    } else {
      toast('Failed to call in patient', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDefer = async(id) => {
    setLoading(true);
    const response = await request('/patient-registeration-defer', {
      method: 'PUT',
      params: id
    });
    if (response?.success) {
      toast(`Successfully Patient's Opd Done`, { variant: 'success' });
      searchPatientDetails();
      DeferPatientDetails();
    } else {
      toast(`Failed Patient's Opd Done`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };


  const handleDone = async (id) => {
    setLoading(true);
    const response = await request('/patient-registeration-done', {
      method: 'PUT',
      params: id
    });
    if (response?.success) {
      toast(`Successfully Patient's Opd Done`, { variant: 'success' });
      searchPatientDetails();
      OpdDonePatientDetails();
    } else {
      toast(`Failed Patient's Opd Done`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleRevert = async (id) => {
    setLoading(true);
    const response = await request('/patient-registeration-revert', {
      method: 'PUT',
      params: id
    });
    if (response?.success) {
      toast(`Successfully Revert Patient`, { variant: 'success' });
      searchPatientDetails();
      OpdDonePatientDetails();
      DeferPatientDetails(); 
    } else {
      toast(`Failed to Revert Patient`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };



  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Queue Management Details
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}></Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', }}>
                  <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 0.4 }}>
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.7 }}
                      name="doctor"
                      disabled={doctorScope.isDoctor}
                      menus={(doctorScope.isDoctor
                        ? doctorDropdown?.data?.filter((doctor) => doctor._id === doctorScope.doctorId)
                        : doctorDropdown?.data
                      )?.map((doctor) => ({
                        ...doctor,
                        name: doctor?.name
                      }))}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 }}>
                    <Typography sx={{ flex: 0.1, fontWeight: 700, fontSize: '1.4rem' }}>No. Of Patient</Typography>
                    <TextField sx={{ flex: 0.2 }} name="no_of_patient" placeholder="No of Patient" disabled  value={detailList.length + deferList.length + opdDoneList.length}/>
                  </Stack>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={7} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.4rem',color:"red" }}>Today's Patients:- {detailList.length}</Typography>
                    <SelectDoctorTable
                      containerSX={{
                        flex: 1,
                        border: '0px solid rgba(0,0,0,0.27)',
                        minHeight: '70vh',
                        border: '1px solid grey'
                      }}
                      data={(watch('doctor') && detailList) || []}
                      columns={DETAIL_HEADER}
                      size="small"
                      showDone={true}
                      showCallIn={true}
                      handleCallIn={handleCallIn}
                      handleDefer={handleDefer}
                      handleDone={handleDone}
                    />
                  </Grid>
                  <Grid item xs={5} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.4rem',color:'red' }}>
                      Today's OPD Done :- {opdDoneList.length}
                    </Typography>
                    <SelectDoctorTable
                      containerSX={{
                        flex: 1,
                        border: '0px solid rgba(0,0,0,0.27)',
                        minHeight: '30vh',
                        border: '1px solid grey',
                        marginBottom: '16px' 
                      }}
                      data={opdDoneList || []}
                      columns={DONE_DETAIL_HEADER}
                      size="small"
                    />
                    <Typography sx={{ fontWeight: 700, fontSize: '1.4rem' ,color:"red"}}>Today's Patient Deferred:- {deferList.length}</Typography>
                    <SelectDoctorTable
                      containerSX={{
                        flex: 1,
                        border: '0px solid rgba(0,0,0,0.27)',
                        minHeight: '30vh',
                        border: '1px solid grey'
                      }}
                      data={deferList || []}
                      columns={DEFER_DETAIL_HEADER}
                      size="small"
                      showRevert={true}
                      handleRevert={handleRevert}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};
